import { ArrowLeft } from "lucide-react"
import { Link } from "react-router"

import { cn } from "~/lib/utils"
import { ForestBackdrop } from "./ForestBackdrops"

// The forest banner directly under the nav on secondary pages (/blog, /faq,
// /solutions, and individual blog posts). Height-matched to the landing
// page's DocsBanner (~224px on desktop) so the top of every page reads the
// same. Same topo data-texture, same wash — or a page-supplied `backdrop`
// (a blog post's own heroImage) in place of it.

export function PageHero({
  eyebrow,
  title,
  backTo,
  backLabel,
  backdrop,
  maxWidth = "max-w-5xl",
  children,
}: {
  eyebrow?: string
  title: string
  /** Back link above the eyebrow, e.g. a post's "← Blog". Omit for none. */
  backTo?: string
  backLabel?: string
  /** Replaces the default ForestBackdrop — a post's own dimmed heroImage. */
  backdrop?: React.ReactNode
  /** Narrow to match a page's own content column (blog posts: max-w-3xl, so
      the title lines up with the article beneath it instead of sitting in
      the wider default column every other PageHero page uses). */
  maxWidth?: string
  children?: React.ReactNode
}) {
  return (
    <div className="relative overflow-hidden bg-forest text-paper">
      {backdrop ?? <ForestBackdrop />}
      <div
        className={cn(
          "relative u-container flex min-h-[224px] flex-col justify-center py-12",
          maxWidth,
        )}
      >
        {backTo ? (
          <Link
            to={backTo}
            className="mb-3 inline-flex w-fit items-center gap-1.5 font-flex font-medium text-[11px] uppercase tracking-[0.14em] text-lime/80 transition-colors hover:text-lime"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            {backLabel}
          </Link>
        ) : null}
        {eyebrow ? (
          <p className="font-flex text-[11px] font-medium uppercase tracking-[0.18em] text-lime/80">
            {eyebrow}
          </p>
        ) : null}
        <h1
          className={cn(
            "font-sans text-4xl font-semibold leading-tight tracking-tight text-paper sm:text-5xl",
            eyebrow && "mt-3",
          )}
        >
          {title}
        </h1>
        {children}
      </div>
    </div>
  )
}
